/**
 * `<TeamsTranscriptPicker>` — pick one of your own recent transcribed Teams
 * meetings and import it onto this candidate (Teams Phase B).
 *
 * Sits alongside the dropzone and the paste box on the Interview Analysis tab.
 * Same modal idiom as `<RegenerateConfirmModal>` (backdrop + centred panel,
 * Esc cancels, backdrop click cancels, default focus on the safe action) —
 * this is not a new pattern.
 *
 * ## Design constraint the copy exists to serve
 *
 * The empty state MUST say that only meetings **with transcription turned on**
 * appear. That is the #1 reason a recruiter's list looks empty and it is not a
 * bug — Teams only produces a transcript when someone started transcription
 * (or the tenant policy did it automatically). Without that sentence the
 * feature reads as broken on first use for most people.
 *
 * ## Null-tolerance is the normal case, not an edge case
 *
 * Under app-only Graph auth `identity.user.displayName` is frequently `null`,
 * so `participants[].display_name` is routinely missing and `subject` can be
 * missing too. Every render path here falls back to counted, neutral copy
 * ("3 participants") rather than emitting blanks or a crash. A meeting whose
 * metadata could not be resolved at all is still listed and still importable —
 * import needs only the ids.
 *
 * ## Recruiter-initiated, never automatic
 *
 * A human picks the meeting and a human is already on the candidate's page.
 * There is deliberately no attendee-email matching anywhere in this component:
 * a candidate joining from a different address would silently attach one
 * person's interview to another person's record.
 */
import { useEffect, useMemo, type JSX } from "react";

import { Button } from "@/components/common/Button";
import { Spinner } from "@/components/common/Spinner";
import { ApiError } from "@/lib/apiError";
import {
  useTeamsTranscripts,
  type TeamsTranscriptRow,
} from "@/hooks/queries/interview-analysis/useTeamsTranscripts";
import {
  formatMeetingDate,
  formatParticipants,
} from "@/components/candidate-page/tabs/teamsTranscriptFormat";

interface TeamsTranscriptPickerProps {
  /** Import one row. The parent owns the mutation + cache write. */
  onImport: (row: TeamsTranscriptRow) => void;
  onClose: () => void;
  /** transcript_id currently importing, if any. */
  importingTranscriptId: string | null;
  /** Import failure to surface in-panel (row-level, not a toast). */
  importError: ApiError | null;
}

export function TeamsTranscriptPicker({
  onImport,
  onClose,
  importingTranscriptId,
  importError,
}: TeamsTranscriptPickerProps): JSX.Element {
  const listQ = useTeamsTranscripts(true);

  useEffect(() => {
    const t = setTimeout(() => {
      document
        .querySelector<HTMLButtonElement>('[data-testid="teams-picker-cancel"]')
        ?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  const isImporting = importingTranscriptId !== null;

  return (
    <div
      className="pointer-events-auto fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="teams-picker-title"
      onKeyDown={handleKeyDown}
      onClick={isImporting ? undefined : onClose}
      data-testid="teams-transcript-picker"
    >
      <div
        className="flex max-h-[80vh] w-full max-w-2xl flex-col gap-4 rounded-lg border border-border-default bg-surface-elevated p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex flex-col gap-1">
          <h2
            id="teams-picker-title"
            className="text-base font-semibold text-text-emphasis"
          >
            Import from Microsoft Teams
          </h2>
          <p className="text-sm text-text-secondary">
            Pick one of your recent transcribed meetings. CertAIn pulls the
            transcript and analyzes it exactly as if you had pasted it.
          </p>
        </header>

        <div className="scroll-primary min-h-[8rem] flex-1 overflow-y-auto">
          <PickerBody
            listQ={listQ}
            onImport={onImport}
            importingTranscriptId={importingTranscriptId}
            importError={importError}
          />
        </div>

        <footer className="flex items-center justify-between gap-3">
          <span className="text-xs text-text-tertiary">
            {listQ.data
              ? `Meetings you organized in the last ${listQ.data.lookback_days} days`
              : ""}
          </span>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isImporting}
            data-testid="teams-picker-cancel"
          >
            Cancel
          </Button>
        </footer>
      </div>
    </div>
  );
}

// ── Body states: loading / error / empty / rows ──────────────────────────────

function PickerBody({
  listQ,
  onImport,
  importingTranscriptId,
  importError,
}: {
  listQ: ReturnType<typeof useTeamsTranscripts>;
  onImport: (row: TeamsTranscriptRow) => void;
  importingTranscriptId: string | null;
  importError: ApiError | null;
}): JSX.Element {
  if (listQ.isPending) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-2 py-7"
        data-testid="teams-picker-loading"
        role="status"
        aria-live="polite"
      >
        <Spinner size="md" />
        <p className="text-sm text-text-secondary">
          Looking for your Teams meetings…
        </p>
      </div>
    );
  }

  if (listQ.isError) {
    return (
      <ListError error={listQ.error} onRetry={() => void listQ.refetch()} />
    );
  }

  const items = listQ.data?.items ?? [];
  if (items.length === 0) {
    return <EmptyState lookbackDays={listQ.data?.lookback_days ?? 30} />;
  }

  return (
    <div className="flex flex-col gap-2" data-testid="teams-picker-list">
      {importError ? (
        <p
          className="rounded-md border border-border-default bg-surface-subtle px-3 py-2 text-sm text-status-rejected-fg"
          role="alert"
          data-testid="teams-picker-import-error"
        >
          {importError.message}
        </p>
      ) : null}
      {items.map((row) => (
        <TranscriptRow
          key={`${row.meeting_id}:${row.transcript_id}`}
          row={row}
          onImport={onImport}
          isImporting={importingTranscriptId === row.transcript_id}
          disabled={
            importingTranscriptId !== null &&
            importingTranscriptId !== row.transcript_id
          }
        />
      ))}
      {listQ.data?.truncated ? (
        <p className="px-1 pt-1 text-xs text-text-tertiary">
          Showing your most recent meetings. Older ones aren&apos;t listed —
          download the transcript from Teams and upload the file instead.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The load-bearing empty state.
 *
 * "No meetings" is a 200 from the server with an explicit reason, not an
 * error, and the first line has to explain the transcription prerequisite —
 * otherwise the overwhelmingly common cause of an empty list reads as a
 * product defect.
 */
function EmptyState({ lookbackDays }: { lookbackDays: number }): JSX.Element {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 px-4 py-7 text-center"
      data-testid="teams-picker-empty"
    >
      <h3 className="text-sm font-semibold text-text-primary">
        No transcribed meetings in the last {lookbackDays} days
      </h3>
      <p className="max-w-md text-sm text-text-secondary">
        Only meetings with{" "}
        <strong className="font-semibold text-text-primary">
          transcription turned on
        </strong>{" "}
        appear here. Teams doesn&apos;t keep a transcript unless someone started
        transcription during the meeting, or your administrator turned it on
        automatically. Recording a meeting is not the same as transcribing it.
      </p>
      <p className="max-w-md text-sm text-text-secondary">
        Only meetings you organized are listed. If a colleague ran the
        interview, ask them to import it, or upload the transcript file
        directly.
      </p>
    </div>
  );
}

function ListError({
  error,
  onRetry,
}: {
  error: ApiError;
  onRetry: () => void;
}): JSX.Element {
  // The server writes the actionable copy for every `teams_*` failure — the
  // access-policy case names the exact administrator fix. Render it verbatim
  // instead of keeping a second, drifting copy of that wording in the client.
  const message =
    error instanceof ApiError && error.error.startsWith("teams_")
      ? error.message
      : "Couldn't reach Microsoft Teams. Try again in a moment.";
  const retryable = !(
    error instanceof ApiError &&
    (error.error === "teams_application_access_policy_missing" ||
      error.error === "teams_consent_not_granted" ||
      error.error === "teams_not_configured" ||
      error.error === "teams_user_not_found")
  );

  return (
    <div
      className="flex flex-col items-center justify-center gap-3 px-4 py-7 text-center"
      role="alert"
      data-testid="teams-picker-error"
    >
      <p className="max-w-md text-sm text-text-primary">{message}</p>
      {retryable ? (
        <Button
          variant="secondary"
          onClick={onRetry}
          data-testid="teams-picker-retry"
        >
          Try again
        </Button>
      ) : (
        <p className="text-xs text-text-tertiary">
          You can still upload the transcript file or paste the text.
        </p>
      )}
    </div>
  );
}

// ── One row ──────────────────────────────────────────────────────────────────

function TranscriptRow({
  row,
  onImport,
  isImporting,
  disabled,
}: {
  row: TeamsTranscriptRow;
  onImport: (row: TeamsTranscriptRow) => void;
  isImporting: boolean;
  disabled: boolean;
}): JSX.Element {
  const when = useMemo(
    () => formatMeetingDate(row.start_at ?? row.created_at),
    [row.start_at, row.created_at],
  );
  const who = useMemo(
    () => formatParticipants(row.participants, row.participant_count),
    [row.participants, row.participant_count],
  );

  return (
    <div
      className="flex items-center justify-between gap-4 rounded-lg border border-border-default bg-surface-card px-4 py-3"
      data-testid="teams-picker-row"
      data-transcript-id={row.transcript_id}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-text-primary">
          {row.subject ?? "Untitled meeting"}
        </span>
        <span className="truncate text-xs text-text-secondary">
          {when}
          {who ? ` · ${who}` : ""}
        </span>
      </div>
      <Button
        variant="primary"
        onClick={() => onImport(row)}
        disabled={disabled || isImporting}
        loading={isImporting}
        data-testid="teams-picker-import"
      >
        {isImporting ? "Importing…" : "Import"}
      </Button>
    </div>
  );
}
